/**
 * DataBest — Backlog list page.
 * Shows tasks that are overdue relative to their frequency, grouped by alert level.
 * Caregiver/Supervisor operational view.
 */
import React, { useEffect, useMemo, useState } from "react";
import ScoringSheet from "@/features/history/components/ScoringSheet";
import useAlertsStore from "@/store/alertsStore";
import { motion } from "framer-motion";
import {
    AlertOctagon, AlertTriangle, Clock, Loader2, ChevronRight,
} from "lucide-react";
import { backlogApi } from "@/lib/backlogApi";
import { formatDateUS } from "@/lib/dates";

const LEVELS = [
    {
        key: "critical",
        label: "Critical",
        icon: AlertOctagon,
        ring: "border-red-300 bg-red-50",
        badge: "bg-red-600 text-white",
        accent: "text-red-700",
    },
    {
        key: "overdue",
        label: "Overdue",
        icon: AlertTriangle,
        ring: "border-orange-300 bg-orange-50",
        badge: "bg-orange-500 text-white",
        accent: "text-orange-700",
    },
    {
        key: "due",
        label: "Due",
        icon: Clock,
        ring: "border-amber-300 bg-amber-50",
        badge: "bg-amber-500 text-white",
        accent: "text-amber-800",
    },
];

const LEVEL_MAP = Object.fromEntries(LEVELS.map((l) => [l.key, l]));

const formatOverdue = (days) => {
    if (days >= 9000) return "Never measured";
    if (days < 1 / 24) return "Just came due";
    if (days < 1) {
        const h = Math.round(days * 24);
        return `${h}h overdue`;
    }
    const d = Math.floor(days);
    return `${d} day${d !== 1 ? "s" : ""} overdue`;
};

const BacklogItem = ({ item, onScore }) => {
    const cfg = LEVEL_MAP[item.level];
    const Icon = cfg.icon;
    return (
        <button
            type="button"
            onClick={() => onScore(item)}
            data-testid={`backlog-item-${item.task.id}`}
            data-level={item.level}
            className={`w-full rounded-2xl border-2 ${cfg.ring} p-4 text-left transition-all hover:scale-[1.005] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-blue-500/40`}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${cfg.badge}`}
                >
                    <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${cfg.badge}`}
                        >
                            {cfg.label}
                        </span>
                        <span className={`text-xs font-semibold ${cfg.accent}`}>
                            {formatOverdue(item.days_overdue)}
                        </span>
                    </div>
                    <p className="mt-1.5 line-clamp-2 font-semibold text-slate-900">
                        {item.task.title}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                        {item.individual.first_name} {item.individual.last_name} ·{" "}
                        <span className="capitalize">
                            {item.outcome.life_area || "—"}
                        </span>{" "}
                        · <span className="capitalize">
                            {item.task.frequency || "daily"}
                        </span>
                        {item.task.frequency_value > 1 &&
                            ` × ${item.task.frequency_value}`}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-400">
                        Expected: {formatDateUS(item.expected_date)}
                    </p>
                </div>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
            </div>
        </button>
    );
};

const BacklogPage = () => {
    const refreshAlerts = useAlertsStore((s) => s.refreshAlerts);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [levelFilter, setLevelFilter] = useState(null);
    const [selectedIndividual, setSelectedIndividual] = useState(null);
    const [page, setPage] = useState(1);
    const [sheetItem, setSheetItem] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const PAGE_SIZE = 25;

    const load = async () => {
        setLoading(true);
        try {
            const r = await backlogApi.list({ limit: 2000 });
            setItems(r.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const counts = useMemo(() => {
        const c = { critical: 0, overdue: 0, due: 0 };
        for (const i of items) c[i.level] = (c[i.level] || 0) + 1;
        c.total = c.critical + c.overdue + c.due;
        return c;
    }, [items]);

    const individualIds = useMemo(
        () => [...new Set(items.map((i) => i.individual.id))],
        [items],
    );

    const individualsById = useMemo(() => {
        const m = {};
        for (const i of items) m[i.individual.id] = i.individual;
        return m;
    }, [items]);

    const filtered = useMemo(() => {
        return items.filter((i) => {
            if (levelFilter && i.level !== levelFilter) return false;
            if (selectedIndividual && i.individual.id !== selectedIndividual)
                return false;
            return true;
        });
    }, [items, levelFilter, selectedIndividual]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = useMemo(
        () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filtered, page],
    );

    // Reset to page 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [levelFilter, selectedIndividual]);

    const handleScore = (item) => {
        setSheetItem({
            task: item.task,
            activity: item.activity,
            outcome: item.outcome,
            individual_id: item.individual.id,
            backfill_date: item.expected_date || null,
        });
        setSheetOpen(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-16 text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-3xl" data-testid="backlog-page">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
            >
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-red-700">
                    Backlog
                </p>
                <h1 className="mt-1 font-heading text-3xl font-bold text-brand-blue-950">
                    Tasks that need your attention
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Sorted by how much they&apos;re past their expected cadence.
                </p>

                {/* Level filters */}
                <div className="mt-6 flex flex-wrap gap-2" data-testid="level-filters">
                    <button
                        onClick={() => setLevelFilter(null)}
                        data-testid="level-filter-all"
                        className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                            !levelFilter
                                ? "bg-brand-blue-700 text-white"
                                : "border border-slate-200 bg-white text-slate-600"
                        }`}
                    >
                        All ({counts.total})
                    </button>
                    {LEVELS.map((L) => {
                        const n = counts[L.key] || 0;
                        const active = levelFilter === L.key;
                        return (
                            <button
                                key={L.key}
                                onClick={() =>
                                    setLevelFilter(active ? null : L.key)
                                }
                                data-testid={`level-filter-${L.key}`}
                                disabled={n === 0}
                                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors disabled:opacity-40 ${
                                    active
                                        ? `${L.badge}`
                                        : "border border-slate-200 bg-white text-slate-600"
                                }`}
                            >
                                {L.label} ({n})
                            </button>
                        );
                    })}
                </div>

                {/* Individual filters (only when >1) */}
                {individualIds.length > 1 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button
                            onClick={() => setSelectedIndividual(null)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                !selectedIndividual
                                    ? "bg-slate-800 text-white"
                                    : "border border-slate-200 bg-white text-slate-600"
                            }`}
                        >
                            All individuals
                        </button>
                        {individualIds.map((id) => (
                            <button
                                key={id}
                                onClick={() => setSelectedIndividual(id)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                    selectedIndividual === id
                                        ? "bg-slate-800 text-white"
                                        : "border border-slate-200 bg-white text-slate-600"
                                }`}
                            >
                                {individualsById[id].first_name}{" "}
                                {individualsById[id].last_name}
                            </button>
                        ))}
                    </div>
                )}

                {/* List */}
                <div
                    className="mt-6 space-y-2.5"
                    data-testid="backlog-list"
                >
                    {filtered.length === 0 ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
                            {items.length === 0
                                ? "Nothing overdue. Great job keeping up!"
                                : "No items match this filter."}
                        </div>
                    ) : (
                        paged.map((it) => (
                            <BacklogItem
                                key={`${it.task.id}-${it.individual.id}-${it.expected_date || ""}`}
                                item={it}
                                onScore={handleScore}
                            />
                        ))
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div
                        className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/85 px-4 py-3 backdrop-blur"
                        data-testid="backlog-pagination"
                    >
                        <div className="text-xs text-slate-600">
                            <span
                                className="font-semibold"
                                data-testid="backlog-pagination-current-page"
                            >
                                Page {page} of {totalPages}
                            </span>
                            <span className="ml-2 text-slate-400">
                                ({filtered.length} total slots)
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                data-testid="backlog-pagination-prev"
                                className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                            >
                                Prev
                            </button>
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                data-testid="backlog-pagination-next"
                                className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </motion.div>

            <ScoringSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                item={sheetItem}
                readOnly={false}
                onSaved={() => {
                    load();
                    refreshAlerts?.();
                }}
            />
        </div>
    );
};

export default BacklogPage;
